// 근무 관리 페이지 (관리자 전용) - v2.1
import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Flex,
  Grid,
  Text,
  useColorModeValue,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Stack,
  Heading,
  useToast,
  Select,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  NumberInput,
  NumberInputField,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
} from '@chakra-ui/react';
import Card from 'components/Card/Card';
import CardHeader from 'components/Card/CardHeader';
import CardBody from 'components/Card/CardBody';
import { 
  collection,
  query,
  where,
  orderBy,
  getDocs,
  doc,
  updateDoc,
  Timestamp
} from 'firebase/firestore';
import { db } from 'config/firebase';
import { getJobPosting, getUserProfile } from 'utils/firestoreUtils';
import { useAuth } from 'contexts/AuthContext';

function WorkManagement() {
  const [assignments, setAssignments] = useState([]);
  const [assignmentsWithData, setAssignmentsWithData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('selected');
  const [selectedAssignment, setSelectedAssignment] = useState(null);

  const [evaluationData, setEvaluationData] = useState({
    actualWorkHours: 0,
    actualWage: 0,
    rating: 5,
    evaluationNote: ''
  });

  const { isOpen, onOpen, onClose } = useDisclosure();
  const authContext = useAuth();
  const userProfile = authContext ? authContext.userProfile : null;
  const toast = useToast();
  const textColor = useColorModeValue('gray.700', 'white');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  const loadAssignments = async () => {
    try {
      setLoading(true);
      
      let q = collection(db, 'assignments');
      
      if (statusFilter !== 'all') {
        q = query(q, where('status', '==', statusFilter), orderBy('confirmedAt', 'desc'));
      } else {
        q = query(q, orderBy('confirmedAt', 'desc'));
      }

      const snapshot = await getDocs(q);
      const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      // 각 배정에 대한 공고와 사용자 정보 가져오기
      const withData = await Promise.all(
        data.map(async (assignment) => {
          const job = await getJobPosting(assignment.jobId);
          const user = await getUserProfile(assignment.userId);
          return { ...assignment, job, user };
        })
      );

      setAssignments(data);
      setAssignmentsWithData(withData);
    } catch (error) {
      console.error('근무자 목록 로딩 오류:', error);
      toast({
        title: '오류',
        description: '근무자 목록을 불러오는데 실패했습니다.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userProfile) {
      loadAssignments();
    }
  }, [statusFilter, userProfile]);

  function handleOpenEvaluation(assignment) {
    if (!assignment) return;
    
    setSelectedAssignment(assignment);
    
    // 이미 완료된 assignment는 기존 값을 불러옴
    if (assignment.status === 'completed') {
      setEvaluationData({
        actualWorkHours: assignment.actualWorkHours || 0,
        actualWage: assignment.actualWage || 0,
        rating: assignment.rating || 5,
        evaluationNote: assignment.evaluationNote || ''
      });
    } else {
      // 새로 완료 처리하는 경우는 기본값 설정
      setEvaluationData({
        actualWorkHours: 0,
        actualWage: assignment.job?.wageAmount * (assignment.workDates?.length || 1) || 0,
        rating: 5,
        evaluationNote: ''
      });
    }
    onOpen();
  }

  function handleCloseModal() {
    onClose();
    // 모달 닫을 때 상태 초기화
    setSelectedAssignment(null);
    setEvaluationData({
      actualWorkHours: 0,
      actualWage: 0,
      rating: 5,
      evaluationNote: ''
    });
  }

  async function handleCompleteWork() {
    try {
      const assignmentRef = doc(db, 'assignments', selectedAssignment.id);
      
      await updateDoc(assignmentRef, {
        status: 'completed',
        actualWorkHours: evaluationData.actualWorkHours,
        actualWage: evaluationData.actualWage,
        rating: evaluationData.rating,
        evaluationNote: evaluationData.evaluationNote,
        completedAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      });

      toast({
        title: '완료 처리 성공',
        description: '근무 완료 처리가 완료되었습니다.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      handleCloseModal();
      loadAssignments();
    } catch (error) {
      console.error('완료 처리 오류:', error);
      toast({
        title: '오류',
        description: '완료 처리에 실패했습니다.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  }

  async function handleNoShow() {
    try {
      const assignmentRef = doc(db, 'assignments', selectedAssignment.id);
      
      await updateDoc(assignmentRef, {
        status: 'canceled',
        cancelReason: '노쇼',
        canceledAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      });

      toast({
        title: '노쇼 처리 완료',
        description: '근무자를 노쇼 처리했습니다.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });

      handleCloseModal();
      loadAssignments();
    } catch (error) {
      console.error('노쇼 처리 오류:', error);
      toast({
        title: '오류',
        description: '노쇼 처리에 실패했습니다.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'selected': return 'blue';
      case 'completed': return 'green';
      case 'canceled': return 'red';
      default: return 'gray';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'selected': return '선정';
      case 'completed': return '완료';
      case 'canceled': return '취소';
      default: return status;
    }
  };

  // 로딩 중
  if (!userProfile) {
    return (
      <Flex direction="column" pt={{ base: '120px', md: '75px' }} align="center" justify="center" minH="50vh">
        <Text>로딩 중...</Text>
      </Flex>
    );
  }

  // 관리자가 아니면 접근 불가
  if (userProfile.role !== 'admin' && userProfile.role !== 'manager') {
    return (
      <Flex direction="column" pt={{ base: '120px', md: '75px' }} align="center">
        <Text>접근 권한이 없습니다.</Text>
      </Flex>
    );
  }

  const stats = {
    total: assignmentsWithData.length,
    selected: assignmentsWithData.filter(a => a.status === 'selected').length,
    completed: assignmentsWithData.filter(a => a.status === 'completed').length,
    totalWages: assignmentsWithData
      .filter(a => a.actualWage)
      .reduce((sum, a) => sum + a.actualWage, 0)
  };

  return (
    <Flex direction="column" pt={{ base: '120px', md: '75px' }}>
      {/* 헤더 */}
      <Flex justify="space-between" align="center" mb={6}>
        <Box>
          <Heading size="lg" color={textColor} mb={1}>
            근무 관리
          </Heading>
          <Text fontSize="sm" color="gray.500">
            확정된 근무를 관리하고 완료 처리합니다
          </Text>
        </Box>
      </Flex>

      {/* 통계 카드 */}
      <Grid
        templateColumns={{ sm: '1fr', md: 'repeat(4, 1fr)' }}
        gap={6}
        mb={6}
      >
        <Card>
          <CardBody>
            <Stat>
              <StatLabel fontSize="sm">전체 배정</StatLabel>
              <StatNumber color="blue.500">{stats.total}</StatNumber>
              <StatHelpText fontSize="xs">건</StatHelpText>
            </Stat>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <Stat>
              <StatLabel fontSize="sm">선정됨</StatLabel>
              <StatNumber color="orange.500">{stats.selected}</StatNumber>
              <StatHelpText fontSize="xs">건</StatHelpText>
            </Stat>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <Stat>
              <StatLabel fontSize="sm">완료</StatLabel>
              <StatNumber color="green.500">{stats.completed}</StatNumber>
              <StatHelpText fontSize="xs">건</StatHelpText>
            </Stat>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <Stat>
              <StatLabel fontSize="sm">총 지급 급여</StatLabel>
              <StatNumber color="purple.500">
                {stats.totalWages.toLocaleString()}
              </StatNumber>
              <StatHelpText fontSize="xs">원</StatHelpText>
            </Stat>
          </CardBody>
        </Card>
      </Grid>

      {/* 필터 */}
      <Card mb={4}>
        <CardBody py={3}>
          <Flex gap={4} align="center">
            <Text fontSize="sm" fontWeight="medium" minW="60px">상태 필터</Text>
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              maxW="200px"
              size="sm"
            >
              <option value="all">전체</option>
              <option value="selected">선정됨</option>
              <option value="completed">완료</option>
              <option value="canceled">취소</option>
            </Select>
          </Flex>
        </CardBody>
      </Card>

      {/* 배정 목록 */}
      <Card overflowX={{ sm: 'scroll', xl: 'hidden' }}>
        <CardHeader p="6px 0px 22px 0px">
          <Heading size="md">근무자 목록</Heading>
        </CardHeader>

        <CardBody>
          {loading ? (
            <Text textAlign="center" py={8}>로딩 중...</Text>
          ) : assignmentsWithData.length === 0 ? (
            <Box textAlign="center" py={12}>
              <Text fontSize="lg" color="gray.500">
                근무자 목록이 없습니다
              </Text>
            </Box>
          ) : (
            <Box overflowX="auto">
              <Table variant="simple" size="md">
                <Thead>
                  <Tr>
                    <Th borderColor={borderColor}>공고명</Th>
                    <Th borderColor={borderColor}>근무자</Th>
                    <Th borderColor={borderColor}>연락처</Th>
                    <Th borderColor={borderColor}>근무일</Th>
                    <Th borderColor={borderColor}>근무 시간</Th>
                    <Th borderColor={borderColor}>급여</Th>
                    <Th borderColor={borderColor}>평점</Th>
                    <Th borderColor={borderColor}>상태</Th>
                    <Th borderColor={borderColor}>관리</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {assignmentsWithData.map((assignment) => (
                    <Tr key={assignment.id}>
                      <Td borderColor={borderColor}>
                        <Text fontSize="sm" fontWeight="medium">
                          {assignment.job?.title || '삭제된 공고'}
                        </Text>
                        <Text fontSize="xs" color="gray.500">
                          {assignment.job?.location}
                        </Text>
                      </Td>
                      <Td borderColor={borderColor}>
                        <Text fontSize="sm" fontWeight="medium">
                          {assignment.user?.name || '-'}
                        </Text>
                      </Td>
                      <Td borderColor={borderColor} fontSize="sm">
                        {assignment.user?.phone || '-'}
                      </Td>
                      <Td borderColor={borderColor} fontSize="xs">
                        {assignment.workDates?.length > 0
                          ? assignment.workDates.length > 1
                            ? `${assignment.workDates[0]} 외 ${assignment.workDates.length - 1}일`
                            : assignment.workDates[0]
                          : '-'
                        }
                      </Td>
                      <Td borderColor={borderColor}>
                        {assignment.actualWorkHours ? (
                          <Text fontSize="sm" fontWeight="medium">
                            {assignment.actualWorkHours}시간
                          </Text>
                        ) : (
                          <Text fontSize="sm" color="gray.400">-</Text>
                        )}
                      </Td>
                      <Td borderColor={borderColor}>
                        <Text fontSize="sm" fontWeight="bold" color={assignment.actualWage ? 'green.500' : 'gray.400'}>
                          {assignment.actualWage
                            ? `${assignment.actualWage.toLocaleString()}원`
                            : '미정'
                          }
                        </Text>
                      </Td>
                      <Td borderColor={borderColor}>
                        {assignment.rating ? (
                          <Text fontSize="sm" fontWeight="bold" color="yellow.500">
                            {assignment.rating.toFixed(1)} ⭐
                          </Text>
                        ) : (
                          <Text fontSize="sm" color="gray.400">-</Text>
                        )}
                      </Td>
                      <Td borderColor={borderColor}>
                        <Badge colorScheme={getStatusColor(assignment.status)} fontSize="xs">
                          {getStatusText(assignment.status)}
                        </Badge>
                      </Td>
                      <Td borderColor={borderColor}>
                        {assignment.status === 'selected' && (
                          <Button
                            size="xs"
                            colorScheme="green"
                            onClick={() => handleOpenEvaluation(assignment)}
                          >
                            완료 처리
                          </Button>
                        )}
                        {assignment.status === 'completed' && (
                          <Button
                            size="xs"
                            variant="outline"
                            onClick={() => handleOpenEvaluation(assignment)}
                          >
                            수정
                          </Button>
                        )}
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          )}
        </CardBody>
      </Card>

      {/* 근무 완료 처리 모달 */}
      <Modal isOpen={isOpen} onClose={handleCloseModal} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            근무 완료 처리
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {selectedAssignment && (
              <Stack spacing={4}>
                {/* 기본 정보 표시 */}
                <Box borderWidth="1px" borderRadius="md" p={4} bg="gray.50" _dark={{ bg: 'gray.700' }}>
                  <Grid templateColumns="repeat(2, 1fr)" gap={3} fontSize="sm">
                    <Box>
                      <Text color="gray.500" mb={1}>근무자</Text>
                      <Text fontWeight="bold">{selectedAssignment.user?.name}</Text>
                    </Box>
                    <Box>
                      <Text color="gray.500" mb={1}>공고</Text>
                      <Text fontWeight="bold">{selectedAssignment.job?.title}</Text>
                    </Box>
                    <Box>
                      <Text color="gray.500" mb={1}>근무일</Text>
                      <Text fontWeight="bold">
                        {selectedAssignment.workDates?.join(', ') || '-'}
                      </Text>
                    </Box>
                    <Box>
                      <Text color="gray.500" mb={1}>계획 급여</Text>
                      <Text fontWeight="bold" color="blue.500">
                        {(selectedAssignment.job?.wageAmount * (selectedAssignment.workDates?.length || 1))?.toLocaleString()}원
                      </Text>
                    </Box>
                  </Grid>
                </Box>

                {/* 실제 근무 시간 */}
                <FormControl isRequired>
                  <FormLabel fontSize="sm">실제 근무 시간</FormLabel>
                  <NumberInput
                    value={evaluationData?.actualWorkHours ?? 0}
                    onChange={(valueString) => setEvaluationData(prev => ({ 
                      ...(prev || { actualWorkHours: 0, actualWage: 0, rating: 5, evaluationNote: '' }), 
                      actualWorkHours: parseFloat(valueString) || 0 
                    }))}
                    min={0}
                    max={24}
                    step={0.5}
                    precision={1}
                  >
                    <NumberInputField placeholder="근무 시간 입력" />
                  </NumberInput>
                  <Text fontSize="xs" color="gray.500" mt={1}>단위: 시간 (예: 8 또는 4.5)</Text>
                </FormControl>

                {/* 실제 지급 급여 */}
                <FormControl isRequired>
                  <FormLabel fontSize="sm">실제 지급 급여</FormLabel>
                  <NumberInput
                    value={evaluationData?.actualWage ?? 0}
                    onChange={(valueString) => setEvaluationData(prev => ({ 
                      ...(prev || { actualWorkHours: 0, actualWage: 0, rating: 5, evaluationNote: '' }), 
                      actualWage: parseInt(valueString) || 0 
                    }))}
                    min={0}
                  >
                    <NumberInputField placeholder="급여 입력" />
                  </NumberInput>
                  <Text fontSize="xs" color="gray.500" mt={1}>단위: 원</Text>
                </FormControl>

                {/* 평점 */}
                <FormControl isRequired>
                  <FormLabel fontSize="sm">평점</FormLabel>
                  <Flex gap={2} align="center">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Button
                        key={star}
                        size="lg"
                        variant={(evaluationData?.rating ?? 5) >= star ? 'solid' : 'outline'}
                        colorScheme="yellow"
                        onClick={() => setEvaluationData(prev => ({ 
                          ...(prev || { actualWorkHours: 0, actualWage: 0, rating: 5, evaluationNote: '' }), 
                          rating: star 
                        }))}
                      >
                        ⭐
                      </Button>
                    ))}
                    <Text ml={2} fontWeight="bold" fontSize="xl">
                      {evaluationData?.rating ?? 5}.0
                    </Text>
                  </Flex>
                </FormControl>

                {/* 평가 메모 */}
                <FormControl>
                  <FormLabel fontSize="sm">평가 메모</FormLabel>
                  <Textarea
                    value={evaluationData?.evaluationNote ?? ''}
                    onChange={(e) => setEvaluationData(prev => ({ 
                      ...(prev || { actualWorkHours: 0, actualWage: 0, rating: 5, evaluationNote: '' }), 
                      evaluationNote: e.target.value 
                    }))}
                    placeholder="근무 태도, 성실성, 특이사항 등을 입력하세요"
                    minH="100px"
                  />
                </FormControl>
              </Stack>
            )}
          </ModalBody>
          <ModalFooter>
            <Button 
              colorScheme="red" 
              variant="outline"
              onClick={handleNoShow}
              mr="auto"
            >
              노쇼 처리
            </Button>
            <Button variant="ghost" mr={3} onClick={handleCloseModal}>
              취소
            </Button>
            <Button
              colorScheme="green"
              onClick={handleCompleteWork}
            >
              완료 처리
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Flex>
  );
}

export default WorkManagement;

